import { createAsyncThunk } from '@reduxjs/toolkit';
import { CommonRootState } from '@common/types/common-root-state-type';
import { GATE_LIST_TTL_MS, shouldRefreshGateList } from './wa-event-gate-refresh';
import { refreshClaudeAgentsActiveThunk, waMessageListenerEvent } from '@common-reducers/backend-api/backend-api';
import {
  DBBotDeploymentThunks,
  DBBotThunks,
  DBWebhooksThunks,
  webhooksSelectByQuery,
} from '@common-reducers/DBServiceThunks';
import { selectIsTeamInboxOpenSomewhere } from '@common/reducers/ConnectedEnginesSlice';
import {
  getInterestedConsumers,
  InterestedConsumers,
  UNIFIED_WA_EVENT_VERSION,
  WaEventBotDispatch,
} from '@bt-common/wa-events/wa-event-relevance';

// ── Event-gate data bootstrap ─────────────────────────────────────────────
// The relevance gate reads the webhook subscription list and the ACTIVE bot
// list. Both load eagerly at extension init (post-auth block in
// backend-api-query-handlers.ts) so the VERY FIRST WhatsApp event is evaluated
// against real data, and stay live afterwards via MQTT entity updates (the
// backend publishes webhooks/bot create/patch/remove to
// users/{owner}/downstream/{path}; NotiferHandlers.processMessage applies
// them). No polling, no per-event fetch on the hot path.
export const loadWaEventGateDataThunk = createAsyncThunk<void, void, {}>(
  'loadWaEventGateDataThunk',
  async (_, thunkApi) => {
    const { dispatch, getState } = thunkApi;
    // ALL non-deleted webhooks ($limit: -1, same pattern as
    // getPendingAndRuningCampaigns). Disabled ones are included so a later
    // enable-toggle patches an entity that already exists in the store.
    trackGateListLoad(dispatch(DBWebhooksThunks.find({ deleted: false, $limit: -1 })));

    // ACTIVE bots (activeDeploymentId set) are what the bot lane matches
    // against — they MUST be present before the first message, along with
    // their deployments (deployed flowJSON is what the trigger matcher
    // prefers). $paginate: false is the codebase-standard shape for these two
    // services (a filtered {activeDeploymentId: {$ne: null}} query 500s over
    // REST — null serializes to '' in the querystring). Other surfaces
    // (Quick Agents sidebar, team inbox) also load them on mount; the
    // itemsLoaded guard makes this a no-op when one of them won the race, so
    // this is purely the guarantee for the event gate.
    const state = getState() as CommonRootState;
    if (!state.DBBotReducer?.itemsLoaded && !state.DBBotReducer?.itemsLoading) {
      dispatch(DBBotThunks.find({ $paginate: false }));
    }
    if (!state.DBBotDeploymentReducer?.itemsLoaded && !state.DBBotDeploymentReducer?.itemsLoading) {
      dispatch(DBBotDeploymentThunks.find({ $paginate: false }));
    }
  },
);

export interface DispatchWaEventArgs {
  /** Canonical taxonomy type (see WA_EVENT_DESCRIPTORS in bt-common). */
  type: string;
  msg: any;
  /** Enriched webhook payload when it differs from msg. */
  webhookPayload?: any;
  /** Bot ids whose trigger scope matched the sender (receivedNewMessage only). */
  matchingBotIds?: string[];
  /** Canonical sender override for the server's bot dispatch. */
  botDispatch?: WaEventBotDispatch;
  /** Sender display name set by the page emit (receivedNewMessage). */
  contactDisplayName?: string;
}

/**
 * THE single client→server send for WhatsApp events.
 *
 * Evaluates the shared relevance predicate (webhooks ∪ bots ∪ team inbox) and
 * either sends ONE v2 envelope to /actions?cmd=wa-message-listener-event or
 * sends nothing at all. Replaces the former three parallel sends
 * (wa-message-listener-event + sendWebhookEvent + trigger-backend-chatbot-event).
 *
 * FAIL OPEN: any error evaluating relevance ⇒ send. A wrong send costs one
 * redundant request; a wrong skip loses the event.
 *
 * Fulfilled payload: the evaluated InterestedConsumers (null when logged out)
 * so callers can gate side effects (e.g. analytics) without re-evaluating.
 */
// BE#304: last time we kicked a background refresh of the gate's webhook list.
// Module-level (the content script is a singleton) so the TTL throttle survives
// across events without touching the generic DB reducer shape.
let lastGateListRefreshAt = 0;
// The gate-list load in flight (init eager load or a TTL refresh), so an event
// the gate would SKIP can wait for it and be judged against the fresh list —
// otherwise the very event that revealed the stale list (e.g. the first group
// event after a webhook was created out-of-instance) is lost.
let gateListLoad: Promise<unknown> | undefined;
/** Bound on that wait: a slow/failed load never holds an event longer. */
export const GATE_LIST_WAIT_MS = 2000;

function trackGateListLoad(result: unknown): void {
  const load = Promise.resolve(result).catch(() => undefined);
  gateListLoad = load;
  void load.then(() => { if (gateListLoad === load) gateListLoad = undefined; });
}

async function waitForGateListLoad(): Promise<boolean> {
  const load = gateListLoad;
  if (!load) return false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timedOut = new Promise<void>((resolve) => { timer = setTimeout(resolve, GATE_LIST_WAIT_MS); });
  try {
    await Promise.race([load, timedOut]);
  } finally {
    clearTimeout(timer);
  }
  return true;
}
// Same TTL throttle for `User.claudeAgentsActive` (read once at login): while it is false,
// a new message re-reads it at most once a minute, so an agent turned on OUTSIDE this tab
// (the website, the API, another device) starts receiving messages without a reload.
let lastAgentsFlagRefreshAt = 0;
// How long a new message waits on that re-read, so the message that triggered it is judged
// with the fresh flag instead of being dropped. A slow/failed re-read just keeps the old flag.
const AGENTS_FLAG_REFRESH_WAIT_MS = 1500;

async function refreshAgentsFlagWithin(dispatch: (a: any) => any, ms: number): Promise<boolean> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timedOut = new Promise<null>((resolve) => { timer = setTimeout(() => resolve(null), ms); });
  try {
    const action = await Promise.race([dispatch(refreshClaudeAgentsActiveThunk()), timedOut]);
    return action?.payload === true;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

export const dispatchWaEventThunk = createAsyncThunk<
  InterestedConsumers | null,
  DispatchWaEventArgs,
  {}
>('dispatchWaEventThunk', async (args, thunkApi) => {
  const { getState, dispatch } = thunkApi;
  const state = getState() as CommonRootState;
  const diag = typeof localStorage !== 'undefined' && localStorage.getItem('BT_WA_DISPATCH_DIAG') === '1';
  if (diag) {
    console.log('[wa-event-dispatch] evaluating', args.type, { hasToken: !!state?.UserReducer?.accessToken });
  }
  if (!state?.UserReducer?.accessToken) return null;

  let consumers: InterestedConsumers = {
    webhooks: true,
    bots: true,
    teamInbox: true,
    aiConversation: true,
    mqttOwnMessages: true,
    mqttInboundMessages: true,
    claudeAgents: true,
    any: true,
  };
  try {
    const latest = getState() as CommonRootState;
    // BE#304: the webhook list is eager-loaded at init by
    // loadWaEventGateDataThunk. Kick a reload when it never loaded OR when it is
    // stale past the TTL — the latter is what lets a webhook created
    // out-of-instance (e.g. via the public v1 API, a dev integration) be honored
    // WITHOUT a manual reload. It only delays an event the gate would otherwise
    // SKIP (see the re-evaluation below).
    if (shouldRefreshGateList(latest.DBWebhooksReducer, lastGateListRefreshAt, Date.now())) {
      lastGateListRefreshAt = Date.now();
      trackGateListLoad(dispatch(DBWebhooksThunks.find({ deleted: false, $limit: -1 })));
    }
    let claudeAgentsActive = latest.UserReducer?.user?.claudeAgentsActive === true;
    if (
      args.type === 'receivedNewMessage' &&
      !claudeAgentsActive &&
      Date.now() - lastAgentsFlagRefreshAt >= GATE_LIST_TTL_MS
    ) {
      lastAgentsFlagRefreshAt = Date.now();
      claudeAgentsActive = await refreshAgentsFlagWithin(dispatch, AGENTS_FLAG_REFRESH_WAIT_MS);
    }
    const evaluate = (latest: CommonRootState): InterestedConsumers => {
    const webhookList = webhooksSelectByQuery(latest.DBWebhooksReducer, { deleted: false }) ?? [];
    const subscribedWebhookEvents = Array.from(
      new Set(
        webhookList.flatMap((w: any) => (w.enabled === false ? [] : (w.events ?? []))),
      ),
    ) as string[];
    return getInterestedConsumers(
      // `aiLinkedChat` is stamped on the lean msg by the linked-chat ingest
      // seam (contentMessageServiceHandlers.handleWaEvent, own-message branch) AFTER the
      // hidden-marker echo guard — it opens the aiConversation bridge lane.
      { type: args.type, fromMe: args.msg?.fromMe === true, aiLinkedChat: args.msg?.aiLinkedChat === true },
      {
        subscribedWebhookEvents,
        matchingBotIds: args.matchingBotIds ?? [],
        teamInboxOpen: selectIsTeamInboxOpenSomewhere(latest),
        // Account-level opt-in (User.preferences.mqttIncludeOwnMessages, off
        // by default) — see wa-event-relevance.ts for why this is a separate
        // lane from the webhook one instead of overloading `webhooks`.
        mqttIncludeOwnMessages: latest.UserReducer?.user?.preferences?.mqttIncludeOwnMessages === true,
        // Account-level opt-in (User.preferences.mqttIncludeInboundMessages,
        // off by default) — makes MQTT independent of any webhook
        // subscription for regular inbound messages too.
        mqttIncludeInboundMessages: latest.UserReducer?.user?.preferences?.mqttIncludeInboundMessages === true,
        // Server-computed capability (rev 3.3 §6.5): the owner has a Claude
        // agent or operator chat → forward new messages in BOTH directions.
        // Read from the authenticated user doc; absent ⇒ lane off (no change).
        claudeAgentsActive,
      },
    );
    };
    consumers = evaluate(latest);
    // Would skip, but the gate list is (re)loading: judge THIS event against the
    // fresh list instead of dropping it (bounded by GATE_LIST_WAIT_MS).
    if (!consumers.any && (await waitForGateListLoad())) {
      consumers = evaluate(getState() as CommonRootState);
    }
  } catch (error) {
    console.warn('[wa-event-dispatch] relevance evaluation failed — failing open (sending)', error);
  }

  if (!consumers.any) {
    // Skips are the steady state when nothing is subscribed (ack storms,
    // unread ticks) — log only under the diag flag to keep the console usable.
    if (diag) {
      const nowS = Math.floor(Date.now() / 1000);
      const socketsSnapshot = ((getState() as CommonRootState).ConnectedEnginesReducer?.sockets ?? []).map(
        (s) => ({ id: s.id, browserType: s.browserType, teamInboxOpen: s.teamInboxOpen, tsAgeSeconds: typeof s.ts === 'number' ? nowS - s.ts : null }),
      );
      console.log('[wa-event-dispatch] skipped — no consumer interested', args.type, { consumers, sockets: socketsSnapshot });
    }
    return consumers;
  }

  dispatch(
    waMessageListenerEvent.initiate({
      v: UNIFIED_WA_EVENT_VERSION,
      type: args.type,
      msg: args.msg,
      webhookPayload: args.webhookPayload,
      contactDisplayName: args.contactDisplayName,
      botDispatch: consumers.bots ? args.botDispatch : undefined,
      hints: {
        webhooks: consumers.webhooks,
        botIds: consumers.bots ? args.matchingBotIds ?? [] : [],
        teamInbox: consumers.teamInbox,
        // WHY this event was sent when everything above is false: the chat is
        // a btgpt-linked AI conversation (fromMe input / poll vote).
        aiConversation: consumers.aiConversation,
        // WHY this event was sent when everything else is false: the account
        // opted its own outgoing messages into MQTT (mqttIncludeOwnMessages).
        mqttOwnMessages: consumers.mqttOwnMessages,
        // WHY this event was sent when everything else is false: the account
        // opted inbound messages into MQTT independent of any webhook
        // (mqttIncludeInboundMessages).
        mqttInboundMessages: consumers.mqttInboundMessages,
        // WHY this event was sent when everything else is false: the owner has
        // a Claude agent or operator chat (User.claudeAgentsActive).
        claudeAgents: consumers.claudeAgents,
      },
    }),
  );
  return consumers;
});
